import { useState, useRef } from 'react'
import { ArrowLeft, Maximize2, Minimize2, Edit3, Rocket } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import SiteGenerator from './SiteGenerator'

function GeneratorPage() {
  const navigate = useNavigate()
  const [phase, setPhase] = useState('describe') // 'describe' or 'iterate'
  const [generatedContent, setGeneratedContent] = useState(null)
  const [formData, setFormData] = useState(null) // Store form data from first phase
  const [isPreviewMaximized, setIsPreviewMaximized] = useState(false)
  const publishFunctionRef = useRef(null)
  const editFunctionRef = useRef(null)

  const handleGenerate = (content, formDataFromGenerator) => {
    setGeneratedContent(content)
    setFormData(formDataFromGenerator) // Store the form data
    setPhase('iterate')
  }

  const handleStartOver = () => {
    setPhase('describe')
    setGeneratedContent(null)
    setFormData(null) // Clear form data
    setIsPreviewMaximized(false)
    publishFunctionRef.current = null
    editFunctionRef.current = null
  }

  if (phase === 'describe') {
    // Phase 1: Full-screen description interface
    return (
      <div className="min-h-screen gradient-bg relative overflow-hidden">
        {/* Cyber Grid Background */}
        <div className="absolute inset-0 bg-cyber-grid bg-cyber-grid opacity-30 pointer-events-none"></div>
        
        {/* Navigation Header */}
        <div className="relative z-20 p-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors duration-200 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-200" />
            <span>Back to Home</span>
          </button>
        </div>

        {/* Full-screen Generator */}
        <div className="px-4 pb-8">
          <SiteGenerator onGenerate={handleGenerate} fullScreen={true} />
        </div>
      </div>
    )
  }

  // Phase 2: Split view (chat + preview)
  return (
    <div className="min-h-screen md:h-screen flex flex-col md:flex-row bg-gray-900 overflow-hidden">
      {/* Left Side - Chat Interface */}
      <div className={`${isPreviewMaximized ? 'hidden md:block md:w-2/5' : 'w-full md:w-2/5'} min-w-0 flex flex-col border-b md:border-b-0 md:border-r border-gray-700 bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 transition-all duration-300`}>
        {/* Chat Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gray-800/50 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors duration-200 group text-sm"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
              <span>Home</span>
            </button>
            <div className="w-px h-4 bg-gray-600"></div>
            <button
              onClick={handleStartOver}
              className="text-sm text-gray-400 hover:text-cyan-400 transition-colors duration-200"
            >
              Start Over
            </button>
          </div>
          <button
            onClick={() => setIsPreviewMaximized(!isPreviewMaximized)}
            className="p-1 text-gray-400 hover:text-white transition-colors duration-200"
          >
            {isPreviewMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>

        {/* Action Buttons */}
        {generatedContent && !isPreviewMaximized && (
          <div className="p-3 md:p-4 border-b border-gray-700 bg-gray-800/30">
            <div className="flex flex-col sm:flex-row gap-2">
                             <button
                 onClick={() => editFunctionRef.current && editFunctionRef.current()}
                 className="flex items-center justify-center px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white rounded-lg transition-colors duration-200 text-sm"
               >
                 <Edit3 className="w-4 h-4 mr-2" />
                 Edit Site
               </button>
               <button
                 onClick={() => publishFunctionRef.current && publishFunctionRef.current()}
                 className="flex items-center justify-center px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white rounded-lg transition-all duration-200 text-sm"
               >
                 <Rocket className="w-4 h-4 mr-2" />
                 Publish Site
               </button>
            </div>
          </div>
        )}

        {/* Chat Content */}
        <div className={`flex-1 overflow-hidden ${isPreviewMaximized ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}>
          <SiteGenerator 
            onGenerate={handleGenerate} 
            fullScreen={false}
            splitView={true}
            initialContent={generatedContent}
            initialFormData={formData} // Pass the form data
            onPublish={(fn) => { publishFunctionRef.current = fn }}
            onShowEditDialog={(fn) => { editFunctionRef.current = fn }}
          />
        </div>
      </div>

      {/* Right Side - Preview */}
      <div className={`${isPreviewMaximized ? 'w-full' : 'w-full md:w-3/5'} flex flex-col bg-white transition-all duration-300`}>
        {/* Preview Header */}
        <div className="hidden md:flex items-center justify-between p-3 bg-gray-100 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              <div className="w-3 h-3 rounded-full bg-red-400"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
              <div className="w-3 h-3 rounded-full bg-green-400"></div>
            </div>
            <span className="text-sm text-gray-600 ml-2">Live Preview</span>
          </div>
          {!isPreviewMaximized && (
            <button
              onClick={() => setIsPreviewMaximized(true)}
              className="p-1 text-gray-500 hover:text-gray-700 transition-colors duration-200"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Preview Content */}
        <div className="flex-1 overflow-hidden">
          {generatedContent ? (
            <iframe
              srcDoc={generatedContent}
              className="w-full h-[60vh] md:h-full border-0"
              title="Generated Site Preview"
              sandbox="allow-scripts allow-same-origin"
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-50">
              <div className="text-center text-gray-500">
                <div className="w-16 h-16 bg-gray-200 rounded-lg mx-auto mb-4 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                </div>
                <p>Your generated site will appear here</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default GeneratorPage 